import type {
  AutoLocateRequestData,
  Base64Result,
  CSVFile,
  JSONUploadState,
  PitchFile,
  TotalStationRequestBody
} from '@frontend/core'
import type { PitchSetupTypeValues } from '@frontend/core/lib/domains/pitches'
import {
  extractBase64,
  getEmptyPitch,
  handleAutoLocate,
  handlePostAnchorPitchSetup,
  isCSVFile,
  isJSONUploadState,
  isPitchFile,
  pitchSetupTypes,
  setCurrentPitchConfig,
  toBase64,
  usePitches_getAllQuery,
  usePitches_getByIdQuery
} from '@frontend/core/lib/domains/pitches'
import { getStrackPitchAndAnchorConfig } from '@frontend/core/lib/domains/sessions'
import type { SportTypeValues } from '@frontend/core/lib/domains/sports'
import { sportTypes } from '@frontend/core/lib/domains/sports'
import { addNotification } from '@frontend/core/lib/modules/notifications'
import { themeAtom, toast } from '@frontend/ui'
import type { CustomFilterState } from '@frontend/utils'
import { Buffer } from 'buffer'
import { useAtomValue } from 'jotai/react'
import { useCallback, useContext, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { useHardware } from '../hardware/hooks'
import { useSelectedOrganisationId } from '../organisations'
import { SessionContext } from '../sessions'
import type { PitchFilters } from './filter'
import { usePitchesFilter } from './filter'

export const usePitches = () => {
  return useAppSelector((state) => state.pitches)
}

export const usePitchInUse = () => {
  const pitches = usePitches()

  const { pitch, anchorConfig, rawPitch } = useMemo(() => {
    return getStrackPitchAndAnchorConfig(pitches.inUse, pitches.inUse?.sport)
  }, [pitches.inUse])

  return {
    pitch: pitch,
    anchorConfig,
    rawPitch
  }
}

export const usePitchesData = (
  customFilterState: CustomFilterState<PitchFilters> = {},
  organisationFiltered = true // whether to filter by selected organisation or not //
) => {
  const organisationId = useSelectedOrganisationId()
  const { exemptFromAuthentication } = useAppSelector(
    (state) => state.app.config
  )
  const shouldFilterByOrg = organisationFiltered && !exemptFromAuthentication
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const query = usePitches_getAllQuery(
    {
      organisationId: shouldFilterByOrg
        ? (organisationId ?? undefined)
        : undefined
    },
    {
      skip: shouldFilterByOrg && !organisationId
    }
  )

  const list = useMemo(() => {
    if (!query.data?.entities) return []
    return Object.values(query.data.entities)
  }, [query.data?.entities])

  // Client side filtering //
  // Gives you filter state, filteredData and functions to update filter values / options //
  const filter = usePitchesFilter(list, customFilterState)

  const selectedSportType = useAppSelector(
    (state) => state.sports.selectedSportType
  )

  const options = useMemo(() => {
    return filter.filteredData
      .filter((pitch) => {
        if (selectedSportType) {
          return (
            pitch.sport === selectedSportType && /[a-zA-Z]/.test(pitch.name)
          )
        }

        // Keep only names with letters (not just numbers/IDs)
        return /[a-zA-Z]/.test(pitch.name)
      })
      .map((pitch) => {
        return {
          name: pitch.name,
          value: pitch.id
        }
      })
  }, [filter.filteredData])

  const map = useMemo(() => {
    return query.data?.entities || {}
  }, [query.data?.entities])

  return {
    ...query,
    ...filter,
    map,
    options,
    selectedId,
    setSelectedId
  }
}

export type PitchesData = ReturnType<typeof usePitchesData>

export const usePitch = (pitchId: string) => {
  const query = usePitches_getByIdQuery(pitchId, {
    skip: !pitchId
  })

  const { selectedSportType } = useAppSelector((state) => state.sports)

  const formatted = useMemo(() => {
    const pitchData = query.data
    if (!pitchData) return getEmptyPitch(selectedSportType)
    return pitchData
  }, [query.data])

  return {
    ...query,
    formatted
  }
}

export const useAnchorPitchSetup = () => {
  const dispatch = useAppDispatch()
  const pitchesState = usePitches()
  const { formattedSession } = useContext(SessionContext)
  const selectedSportType = useAppSelector(
    (state) => state.sports.selectedSportType
  )

  const { formattedHardware } = useHardware()

  const handleAnchorPitchSetup = useCallback(
    (
      type: PitchSetupTypeValues,
      formData?: CSVFile | PitchFile | JSONUploadState
    ): void => {
      const {
        side,
        setupTagList,
        secondPitchSetupTagList,
        inUse,
        demonstrationPitch
      } = pitchesState

      switch (type) {
        case pitchSetupTypes.items.pitch.value:
          if (formData && isPitchFile(formData))
            dispatch(setCurrentPitchConfig(formData.selectedPitchId))
          break
        case pitchSetupTypes.items.auto.value:
          {
            if (!inUse) return

            const autoData: AutoLocateRequestData = {
              direction: side,
              pitchId: inUse.id,
              tagList: null,
              type: demonstrationPitch
                ? (sportTypes.items.demonstration.value as SportTypeValues)
                : !formattedSession.sport.isUnknown()
                  ? (formattedSession.sport.value as SportTypeValues)
                  : (selectedSportType as SportTypeValues)
            }

            if (
              side === 0 &&
              (!setupTagList[0] || !setupTagList[1] || !setupTagList[2])
            ) {
              toast.error('Tags not set for full pitch setup')
              return
            }

            if (
              side === 0 &&
              pitchesState.doublePitchSetup &&
              !secondPitchSetupTagList[1]
            ) {
              toast.error('Tags not set for second pitch setup')
              return
            }

            if (side === 0) {
              autoData.tagList = setupTagList

              if (pitchesState.doublePitchSetup) {
                autoData.tagList = setupTagList.concat(secondPitchSetupTagList)
              }

              autoData.tagList = autoData.tagList.filter(
                (tag: string | null) => tag !== null
              )
            }

            // Check if anchors are fluctuating //
            const fluctuatingAnchors: Record<string, string> = {}
            formattedHardware.types.anchor.devices.list.forEach((anchor) => {
              if (anchor.hasFluctuated) {
                fluctuatingAnchors[anchor.id] = anchor.id
              }
            })

            if (Object.keys(fluctuatingAnchors).length > 0) {
              dispatch(
                addNotification({
                  type: 'info',
                  displayType: 'modal',
                  message: `Anchors ${Object.values(fluctuatingAnchors).join(
                    ', '
                  )} distances to master are fluctuating. Are you sure you want to run autosetup?`,
                  title: 'Anchors Unstable',
                  action: {
                    label: 'Yes',
                    onClick: () => dispatch(handleAutoLocate(autoData))
                  }
                })
              )
            } else {
              dispatch(handleAutoLocate(autoData))
            }
          }
          break
        case pitchSetupTypes.items.totalStation.value:
          if (formData && isCSVFile(formData)) {
            const anchorCsvToBase64 = toBase64(
              formData.anchorFileWithoutIgnored,
              'anchor'
            )

            const pitchCsvToBase64 = toBase64(
              formData.pitchFileWithoutIgnored,
              'pitch'
            )

            Promise.all([anchorCsvToBase64, pitchCsvToBase64]).then(
              (results: Base64Result[]) => {
                const anchorBase64 = extractBase64(results, 'anchor')
                const pitchBase64 = extractBase64(results, 'pitch')

                // The separate pitch CSV is optional, so a null pitchBase64
                // is a valid submission — only the anchor CSV is required.
                if (anchorBase64 == null) return

                const data: TotalStationRequestBody = {
                  anchorBase64: anchorBase64,
                  anchorExtension: formData.anchorExtension,
                  pitchBase64: pitchBase64,
                  pitchExtension: formData.pitchExtension,
                  type: demonstrationPitch
                    ? (sportTypes.items.demonstration.value as SportTypeValues)
                    : !formattedSession.sport.isUnknown()
                      ? (formattedSession.sport.value as SportTypeValues)
                      : (selectedSportType as SportTypeValues),
                  save: formData.save,
                  hardwareGeneration: formData.hardwareGeneration ?? 1
                }

                if (formData.save) data.name = formData.name

                dispatch(handlePostAnchorPitchSetup(data))
              }
            )
          }
          break
        case pitchSetupTypes.items.uploadJSON.value:
          if (formData && isJSONUploadState(formData)) {
            if (!formData.anchorJSON) return
            const anchorBase64 = Buffer.from(
              JSON.stringify(formData.anchorJSON)
            ).toString('base64')

            let pitchBase64: string | undefined = undefined

            if (formData.coordinateJSON) {
              pitchBase64 = Buffer.from(
                JSON.stringify(formData.coordinateJSON)
              ).toString('base64')
            }

            const data: TotalStationRequestBody = {
              anchorBase64: anchorBase64,
              anchorExtension: 'application/json',
              pitchBase64: pitchBase64,
              pitchExtension: 'application/json',
              type: demonstrationPitch
                ? (sportTypes.items.demonstration.value as SportTypeValues)
                : !formattedSession.sport.isUnknown()
                  ? (formattedSession.sport.value as SportTypeValues)
                  : (selectedSportType as SportTypeValues),
              save: false,
              hardwareGeneration: 1
            }
            dispatch(handlePostAnchorPitchSetup(data))
          }
          break
      }
    },
    [pitchesState, formattedSession.sport, dispatch, formattedHardware]
  )

  return { handleAnchorPitchSetup }
}

// Utility hook for pitch canvas fill and line colors
// Can be customized here

export const usePitchCanvasColors = () => {
  const theme = useAtomValue(themeAtom)

  return useMemo(
    () => ({
      fill: 'transparent',
      line: theme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)'
    }),
    [theme]
  )
}
